import React from 'react'
import { Stack, Text } from '@chakra-ui/react'
import { LabeledSelect, FormLabel, RadioGroup, Radio } from '@apex/component-library'
import { ContributionConstraints } from '@Types'
import { removeUnderscores } from '../../utils'
import { useFormikContext } from 'formik'
import { ContributionValues, PREVIOUS, CURRENT } from './types'
import { DateTime } from 'luxon'

export interface IraContributionFormProps {
  constraints: ContributionConstraints | undefined | null
}

const IraContributionForm = ({ constraints }: IraContributionFormProps) => {
  const formik = useFormikContext<ContributionValues>()
  const contributionReasonOptions = Object.values((constraints as ContributionConstraints).validContributions).map(
    (type) => {
      return {
        key: `distribution-reason-${type?.reason}`,
        text: removeUnderscores(type?.reason),
        value: type?.reason
      }
    }
  )
  const selectedPreviousYearAllowed = Object.values((constraints as ContributionConstraints).validContributions).find(
    (type) => type.reason === formik.values.contributionType
  )?.previousYearContributionAllowed
  const contributionDeadline = Object.values((constraints as ContributionConstraints).validContributions).find(
    (type) => type.reason === formik.values.contributionType
  )?.previousYearDeadline
  const isPastDeadline = contributionDeadline
    ? DateTime.local().startOf('day') > DateTime.fromFormat(contributionDeadline, 'yyyy-MM-dd')
    : false
  const displayContributionYearOption = selectedPreviousYearAllowed && !isPastDeadline
  return (
    <>
      <Text as='h6' data-testid='contribution-header'>
        IRA Contributions require the following information:
      </Text>
      <LabeledSelect
        label='Contribution Type'
        aria-label='Contribution Type'
        name='contributionType'
        isRequired
        onChange={(e) => {
          const doesSelectedTypeAllowPrevious = constraints?.validContributions.find(
            (item) => item.reason === e.target.value
          )?.previousYearContributionAllowed
          formik.setValues({
            ...formik.values,
            contributionType: e.target.value,
            contributionYear: doesSelectedTypeAllowPrevious ? 'CURRENT' : ''
          })
        }}
        isInvalid={!!formik.errors.contributionType && formik.touched.contributionType}
        errorMessage={formik.errors.contributionType}
      >
        <option value=''></option>
        {contributionReasonOptions.map((option) => (
          <option key={option.key} value={option.value}>
            {option.text}
          </option>
        ))}
      </LabeledSelect>
      {formik.values.contributionType && displayContributionYearOption && (
        <Stack>
          <FormLabel fontWeight='semibold'>Contribution Year *</FormLabel>
          <RadioGroup
            name='contributionYear'
            onChange={(val) => {
              formik.setFieldValue('contributionYear', val)
            }}
            value={formik.values.contributionYear}
          >
            <Radio value={CURRENT}>Current Year</Radio>
            <Radio value={PREVIOUS}>Previous Year</Radio>
          </RadioGroup>
        </Stack>
      )}
      {formik.values.contributionType && !displayContributionYearOption && (
        <Stack spacing={2}>
          <FormLabel fontWeight='semibold'>Contribution Year *</FormLabel>
          <Text>The contribution will be applied to the current year.</Text>
        </Stack>
      )}
    </>
  )
}

export default IraContributionForm
